<template>
  <div>
    <!-- 
	<div>
		<code>{{ current_step }} / {{ steps_count }}</code>
	</div> 
    -->

    <v-stepper
      v-model="current_step"
      ref="stepper"
      @change="stepChanged"
      class="elevation-0 transparent"
      light
    >
      <v-container fluid style="background-color: #fff">
        <v-stepper-header class="editor-stepper-header mx-auto">
          <template v-for="(step, i) in steps">
            <v-stepper-step
              class="text--white"
              color="primary"
              :key="`title-${step.num}`"
              :step="step.num"
              :complete="completed_steps[i]"
              :editable="editable_steps[i]"
              :rules="stepper_rules[i]"
              edit-icon="mdi-check"
            ></v-stepper-step>

            <v-divider v-if="i !== steps.length - 1" :key="step.num" class="primary"></v-divider>
          </template>
        </v-stepper-header>
      </v-container>

      <v-stepper-items>
        <v-container>
          <v-stepper-content
            v-for="(step,i) in steps"
            :step="step.num"
            :key="`content-${step.num}`"
          >
            <div class="pa-1">
              <v-card class="mb-12" :elevation="2">
                <v-card-text v-if="step.fields">
                  <h2 class="title">{{step.title}}</h2>
                  <v-container fluid grid-list-xl>
                    <v-row>
                      <input-factory
                        v-for="(field,key) in step.fields"
                        :key="key"
                        :config="field"
                        @updateNeeded="onReload"
                        @input="onInput"
                      ></input-factory>
                    </v-row>
                  </v-container>

                  <v-divider v-if="step.component"></v-divider>

                  <v-form ref="profile_form" v-if="step.component">
                    <v-container fluid>
                      <v-row cols="12">
                        <v-col sm="12" md="6">
                          <!-- <h3 class="mb-3">{{ trans('user.profile_contacts') }}</h3> -->

                          <v-text-field
                            :disabled="is_profile_filled"
                            v-model="email"
                            :rules="validation_rules.email"
                            :label="trans('user.email')"
                            :error="hasError('email')"
                            :error-messages="getError('email')"
                            @input="unsetError('email')"
                            outlined
                          ></v-text-field>

                          <v-text-field
                            :disabled="is_profile_filled"
                            v-model="phone_number"
                            :rules="validation_rules.phone"
                            :label="trans('user.phone_number')"
                            :error="hasError('phone_number')"
                            :error-messages="getError('phone_number')"
                            @input="unsetError('phone_number')"
                            outlined
                          ></v-text-field>

                          <v-text-field
                            :disabled="is_profile_filled"
                            v-model="first_name"
                            :rules="validation_rules.first_name"
                            :label="trans('user.first_name')"
                            :error="hasError('first_name')"
                            :error-messages="getError('first_name')"
                            @input="unsetError('first_name')"
                            outlined
                          ></v-text-field>

                          <v-text-field
                            :disabled="is_profile_filled"
                            v-model="last_name"
                            :label="trans('user.last_name')"
                            :error="hasError('last_name')"
                            :error-messages="getError('last_name')"
                            @input="unsetError('last_name')"
                            outlined
                          ></v-text-field>
                        </v-col>
                        <v-col sm="12" md="6">
                          <!-- <h3 class="mb-3">{{ trans('user.profile_privacy') }}</h3> -->

                          <v-checkbox
                            :disabled="is_profile_filled"
                            v-model="email_visible"
                            :false-value="false"
                            :true-value="true"
                            :label="trans('user.email_visible')"
                          ></v-checkbox>

                          <v-checkbox
                            :disabled="is_profile_filled"
                            v-model="phone_number_visible"
                            :false-value="false"
                            :true-value="true"
                            :label="trans('user.phone_number_visible')"
                          ></v-checkbox>

                          <v-checkbox
                            :disabled="is_profile_filled"
                            v-model="last_name_visible"
                            :false-value="false"
                            :true-value="true"
                            :label="trans('user.last_name_visible')"
                          ></v-checkbox>
                        </v-col>
                      </v-row>
                    </v-container>

                    <v-container fluid v-if="step.component && use_additional_profile" class="py-0">
                      <v-row>
                        <v-col cols="12" class="py-0">
                          <v-checkbox
                            v-model="use_different_profile"
                            :false-value="false"
                            :true-value="true"
                            :label="trans('advert.use_different_profile')"
                          ></v-checkbox>
                        </v-col>
                      </v-row>

                      <input-factory :config="different_profile_schema" @input="onInput"></input-factory>
                    </v-container>
                  </v-form>
                </v-card-text>

                <v-card-actions class="px-6 pb-6">
                  <v-spacer></v-spacer>
                  <v-btn
                    color="secondary"
                    :disabled="!can_go_back"
                    @click="prev"
                    outlined
                    rounded
                  >{{ trans('app.back') }}</v-btn>
                  <v-btn
                    color="primary"
                    :disabled="!can_go_forward"
                    v-if="i != steps_count - 1"
                    @click="next"
                    depressed
                    rounded
                  >{{ trans('app.continue') }}</v-btn>
                  <v-btn color="primary" v-if="can_save" fab depressed @click="save" rounded>Save</v-btn>
                </v-card-actions>
              </v-card>
            </div>
          </v-stepper-content>
        </v-container>
      </v-stepper-items>
    </v-stepper>
  </div>
</template>

<script>
import _ from "lodash";
import Vue from "vue";
import { mapGetters, mapActions, mapState } from "vuex";
import InlineProfileEditor from "../ui/custom/InlineProfileEditor";
import DifferentProfile from "../ui/input/DifferentProfile";

let regex_email = new RegExp(
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
);
let regex_phone = new RegExp(
  /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/i
);

export default {
  components: {
    "profile-editor": InlineProfileEditor
  },

  props: {
    schema: {
      type: Object,
      default: () => ({})
    },
    editable: {
      type: Boolean,
      default: false
    },

    // user: {
    // 	type: Object,
    // 	default: () => ({}),
    // },
    use_additional_profile: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    current_step: 0,
    visited_steps: 0,
    can_save: false,
    loaded: false

    // use_different_profile: false,
    // different_profile: {},
  }),

  computed: {
    ...mapGetters({
      getError: "errors/get",
      hasError: "errors/has"
    }),
    ...mapState("auth", {
      authenticated: "authenticated"
    }),
    ...mapState("user", {
      user: "instance"
    }),

    steps: function() {
      let schema = _.get(this.$props, "schema", []);
      let output = [];
      let counter = 1;
      _.each(schema, (data, key) => {
        let enabled_fields = _.filter(data, f => f.enabled == true);
        if (_.size(enabled_fields)) {
          let fieldset = {
            num: counter,
            title: this.trans(key),
            fields: data
            // component: null,
          };
          output.push(fieldset);
          counter++;
        }
      });

      output = _.map(output, (fieldset, index) => {
        if (index == _.size(output) - 1) fieldset.component = true;
        return fieldset;
      });

      /*
			if (this.is_profile_unfilled) {
				let fieldset = {
					num: counter,
					title: this.trans('user.profile_contacts'),
					fields: null,
					component: true,
				}
				output.push(fieldset);
				counter++;
			}
			*/

      // ...
      if (this.current_step > counter) {
        this.current_step = counter;
      }
      return output;
    },

    different_profile_schema: function() {
      let schema = _.get(this.$props, "schema", []);
      let default_profile = Object.assign(
        {},
        {
          phone_number: this.phone_number,
          first_name: this.first_name,
          last_name: this.last_name
        }
      );
      let def_config = {
        presentation: "hidden",
        view: DifferentProfile,
        size: { cols: 12 },
        enabled: false
      };
      let config = null;
      try {
        config = _.find(
          schema["advert.group_advert_details"],
          e => e.id == "different_profile"
        );
      } catch (e) {}
      config = Object.assign({}, config, def_config);
      config.enabled = this.use_different_profile;
      return config;
    },

    steps_count: function() {
      return _.size(this.steps);
    },

    completed_steps: function() {
      let completed = [];
      _.each(this.steps, (step, i) => {
        completed.push(this.current_step > i + 1);
      });
      return completed;
    },

    editable_steps: function() {
      let editable = [];
      _.each(this.steps, (step, i) => {
        editable.push(
          this.current_step > i + 1 ||
            this.visited_steps > i ||
            this.$props.editable == true
        );
      });
      return editable;
    },

    can_go_forward: function() {
      return !this.is_last_step;
    },

    can_go_back: function() {
      return !this.is_first_step;
    },

    is_first_step: function() {
      return this.current_step == 1;
    },

    is_last_step: function() {
      return this.current_step == this.steps_count;
    },

    is_profile_filled: function() {
      return _.get(this.user, "filled", false);
    },

    is_profile_unfilled: function() {
      return !this.is_profile_filled;
    },

    stepper_rules: function() {
      let rules = [];
      let schema = _.get(this.$props, "schema", []);
      _.each(schema, (fields, i) => {
        let has_errors = false;
        _.each(fields, field => {
          if (this.hasError(field.id)) has_errors = true;
        });
        if (has_errors) {
          rules.push([() => false]);
        } else {
          rules.push([() => true]);
        }
      });
      return rules;
    },

    first_with_error: function() {
      let first_with_error = null;
      let counter = 1;
      let schema = _.get(this.$props, "schema", []);
      _.each(schema, (fields, i) => {
        let has_errors = false;
        _.each(fields, field => {
          if (this.hasError(field.id)) {
            if (!first_with_error) first_with_error = counter;
          }
        });
        counter++;
      });
      if (first_with_error > this.steps.length)
        first_with_error = this.steps.length;
      return first_with_error;
    },

    // user profile
    email: {
      get: function() {
        return _.get(this.user, "email", null);
      },
      set: function(v) {
        this.setUserParam({ email: v });
      }
    },
    phone_number: {
      get: function() {
        return _.get(this.user, "phone_number", null);
      },
      set: function(v) {
        if (regex_phone.test(v)) {
          let start_digit = new RegExp(/^(\d)/i);
          if (start_digit.test(v)) {
            v = v.replace(/^(\d)/i, "+$1");
          }
        }
        v = v.replace(/[^\d\+]/g, "");

        this.setUserParam({ phone_number: v });
      }
    },
    first_name: {
      get: function() {
        return _.get(this.user, "first_name", null);
      },
      set: function(v) {
        this.setUserParam({ first_name: v });
      }
    },
    last_name: {
      get: function() {
        return _.get(this.user, "last_name", null);
      },
      set: function(v) {
        this.setUserParam({ last_name: v });
      }
    },

    email_visible: {
      get: function() {
        return !!_.get(this.user, "email_visible", false);
      },
      set: function(v) {
        this.setUserParam({ email_visible: !!v });
      }
    },
    phone_number_visible: {
      get: function() {
        return !!_.get(this.user, "phone_number_visible", false);
      },
      set: function(v) {
        this.setUserParam({ phone_number_visible: !!v });
      }
    },
    last_name_visible: {
      get: function() {
        return !!_.get(this.user, "last_name_visible", false);
      },
      set: function(v) {
        this.setUserParam({ last_name_visible: !!v });
      }
    },

    use_different_profile: {
      get() {
        let val = _.get(
          this.$store.state,
          "adverts.instance.use_different_profile",
          false
        );
        return !!val;
      },
      set(val) {
        this.$emit("input", { use_different_profile: !!val });
        this.$nextTick(() => {
          this.$emit("input", { use_different_profile: !!val });
        });
        // this.$emit('reload');
      }
    },

    phone_number_alt: {
      get() {
        return _.get(
          this.$store.state,
          "adverts.instance.different_profile.phone_number",
          this.phone_number
        );
      },
      set(v) {
        this.$emit("input", { different_profile: { phone_number: v } });
      }
    },

    first_name_alt: {
      get() {
        return _.get(
          this.$store.state,
          "adverts.instance.different_profile.first_name",
          this.first_name
        );
      },
      set(v) {
        this.$emit("input", { different_profile: { first_name: v } });
      }
    },

    last_name_alt: {
      get() {
        return _.get(
          this.$store.state,
          "adverts.instance.different_profile.last_name",
          this.last_name
        );
      },
      set(v) {
        this.$emit("input", { different_profile: { last_name: v } });
      }
    },

    validation_rules: function() {
      return {
        email: [
          () => regex_email.test(this.email) || "Invalid email address",
          () => !!this.email || "Invalid email address"
        ],
        phone: [
          () => !!this.phone_number || "This field is required",
          () =>
            this.phone_number
              ? regex_phone.test(this.phone_number) || "Invalid phone number"
              : true
        ],
        first_name: [() => !!this.first_name || "This field is required"],
        // email_alt: this.use_different_profile ? [
        // 	() => regex_email.test(this.different_profile.email) || 'Invalid email address',
        // 	() => !!this.different_profile.email || 'Invalid email address',
        // ] : () => true,
        phone_alt: this.use_different_profile
          ? [
              () => !!this.phone_number_alt || "This field is required",
              () =>
                regex_phone.test(this.phone_number_alt) ||
                "Invalid phone number"
            ]
          : [true],
        first_name_alt: this.use_different_profile
          ? [() => !!this.first_name_alt || "This field is required"]
          : [true]
      };
    }
  },

  methods: {
    ...mapActions("user", {
      setUserParam: "updateInstance",
      loadUserProfile: "edit",
      saveUserProfile: "update"
    }),

    ...mapActions("errors", {
      unsetError: "unset",
      putError: "put"
    }),

    ...mapActions("adverts", {
      updateInstance: "updateInstance"
    }),

    next: function() {
      if (this.can_go_forward) {
        this.current_step++;
        if (this.visited_steps < this.current_step) {
          this.visited_steps = this.current_step;
        }
      }
      this.stepChanged(this.current_step);
    },

    prev: function() {
      if (this.can_go_back) this.current_step--;
      this.stepChanged(this.current_step);
    },

    onInput: function(payload) {
      this.$emit("input", payload);
    },

    onReload: function() {
      this.$emit("reload");
    },

    stepChanged: function(step) {
      if (this.$props.editable == true) {
        this.can_save = true;
      } else if (this.is_last_step == true) {
        this.can_save = true;
      } else if (this.visited_steps >= this.steps_count) {
        this.can_save = true;
      } else {
        this.can_save = false;
      }
      this.$emit("change", step);
    },

    save: function() {
      if (this.is_profile_unfilled) {
        this.$forceUpdate();

        this.saveUserProfile(this.user);
      } else {
        this.$emit("save");
      }
    },

    onProfileSaved: function() {
      this.$emit("save");
    }

    /*    	
		setProfileEditable(flag) {
    		this.use_different_profile = flag;
    		_.each(this.$refs.profile_form.inputs, (input) => {
    			input.disabled = (!flag);
    		})
    	}
    	*/
  },

  mounted: function() {
    if (this.authenticated && !this.user) {
      this.loadUserProfile();
    }

    this.stepChanged(this.current_step);

    this.$store.subscribe((mutation, state) => {
      if (mutation.type == "user/handleStatus" && state.status == "saved") {
        // this.$emit('save');
      }

      if (mutation.type == "user/handleEdit") {
        // if (this.is_profile_unfilled == true) {
        // 	this.use_different_profile = false;
        // }
      }

      // if (mutation.type == 'preferences/handleUpdateInstance') {

      // }

      if (mutation.type == "user/handleSaved") {
        this.$nextTick(function() {
          try {
            this.$refs.profile_form.forEach(comp => {
              comp.validate();
            });
          } catch (e) {
            console.log(e);
          }

          if (this.is_profile_unfilled == true) {
            this.putError({
              key: "error",
              value: this.trans("user.profile_incomplete_message")
            });
          } else {
            this.$emit("save");
          }
        });
      }

      if (mutation.type == "adverts/handleStatus") {
        if (
          state.adverts.instance_saving == true &&
          mutation.payload.instance_saved == false
        ) {
          // this.current_step = this.first_with_error;
          this.current_step = 0;
          this.$nextTick(function() {
            this.current_step = this.first_with_error;
          });
        }
      }

      if (mutation.type == "preferences/handleStatus") {
        if (
          state.preferences.instance_saving == true &&
          mutation.payload.instance_saved == false
        ) {
          // this.current_step = this.first_with_error;
          this.current_step = 0;
          this.$nextTick(function() {
            this.current_step = this.first_with_error;
          });
        }
      }

      if (
        mutation.type == "adverts/handleStatus" ||
        mutation.type == "preferences/handleStatus"
      ) {
        if (mutation.payload.instance_loaded == true) {
          if (!this.loaded) {
            let silent = Vue.config.silent;
            if (silent == false) Vue.config.silent = true;

            let stepper = this.$refs.stepper;
            if (stepper !== undefined) {
              this.current_step = 0;
              this.$nextTick(function() {
                this.current_step = 1;
                try {
                  // Issue # FLATS-SCS-1
                  // Cannot set property 'value' of undefined
                  setTimeout(() => {
                    stepper.value = 0;
                  }, 500);
                } catch (e) {
                  /* Avoid mutating a prop directly... Yes but stepper doesn't work properly. */
                }
                try {
                  // Issue # FLATS-SCS-2
                  // Cannot set property 'value' of undefined
                  setTimeout(() => {
                    stepper.value = 1;
                    this.loaded = true;
                  }, 600);
                } catch (e) {
                  /* Avoid mutating a prop directly... Yes but stepper doesn't work properly. */
                }
              });
            } else {
              this.current_step = 1;
            }
          }
        }
      }
    });
  }
};
</script>

<style lang="scss">
</style>